/**
 * ItemChips — shared component that renders every "chip" derived from
 * an item's data (§9 Pack redesign, §13/§14 Hero UI).
 *
 * Sources it consumes:
 *   • gm_effects[] (structured Fase 2C effects: stat, resistance, weakness,
 *     max_hp, max_mana, max_vigor, max_conc, element_damage, remove_dot,
 *     remove_status)
 *   • element/element_damage (weapon-side flat elemental damage)
 *   • resistance_element/resistance_value (armor/trinket-side flat resist)
 *   • elemental_damage{} / elemental_resistance{} (legacy affix map)
 *   • stat_bonuses{} or stat/stat_bonus (legacy per-stat bonus)
 *   • requirements text
 *
 * Each chip carries its own color scheme (fire orange / ice blue / holy
 * yellow / …). Elements without a known color fall back to gold.
 */
import { Flame, Shield, Zap, Sparkles, Skull, Heart } from "lucide-react";

const ELEMENT_COLOR = {
  Fire: "#E55A00", Ice: "#8FD1E5", Lightning: "#F7D64A",
  Poison: "#7CB342", Darkness: "#5D2F5F", Holy: "#E5C34A",
  Arcane: "#A97AB0", Earth: "#8B6E47", Water: "#4A6FA5", Wind: "#B0C4B1",
};

function elColor(el) {
  return ELEMENT_COLOR[el] || "#E5C34A";
}

function Chip({ label, color = "#B89C3A", Icon = null, testid, kind = "" }) {
  return (
    <span data-testid={testid} data-chip-kind={kind}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono text-[9px] tracking-wider uppercase whitespace-nowrap rounded-sm"
      style={{ borderColor: color, color, backgroundColor: color + "12" }}>
      {Icon && <Icon className="w-2.5 h-2.5" strokeWidth={2} />}
      {label}
    </span>
  );
}

export default function ItemChips({ item, compact = false }) {
  if (!item) return null;
  const chips = [];
  // ── Elemental damage (weapon side) ──
  if (item.element && item.element_damage) {
    chips.push(
      <Chip key="el-dmg" testid="chip-element-damage" kind="element-damage"
        label={`+${item.element_damage} ${item.element}`}
        color={elColor(item.element)} Icon={Flame} />
    );
  }
  // ── Elemental resistance (armor / trinket side) ──
  if (item.resistance_element && item.resistance_value) {
    chips.push(
      <Chip key="el-res" testid="chip-element-resistance" kind="element-resistance"
        label={`+${item.resistance_value}% ${item.resistance_element} Res`}
        color={elColor(item.resistance_element)} Icon={Shield} />
    );
  }
  // ── Legacy elemental_damage / elemental_resistance maps ──
  Object.entries(item.elemental_damage || {}).forEach(([el, v]) => {
    chips.push(
      <Chip key={"led" + el} testid={`chip-el-dmg-${el}`} kind="element-damage"
        label={`+${v} ${el}`} color={elColor(el)} Icon={Flame} />
    );
  });
  Object.entries(item.elemental_resistance || {}).forEach(([el, v]) => {
    chips.push(
      <Chip key={"ler" + el} testid={`chip-el-res-${el}`} kind="element-resistance"
        label={`+${Math.round(v * 100)}% ${el} Res`} color={elColor(el)} Icon={Shield} />
    );
  });
  // ── Structured gm_effects ──
  (item.gm_effects || []).forEach((eff, i) => {
    if (!eff || typeof eff !== "object") return;
    const val = eff.value;
    switch (eff.kind) {
      case "stat":
        chips.push(<Chip key={"gse" + i} testid={`chip-stat-${eff.stat}`} kind="stat"
          label={`+${val} ${String(eff.stat || "").slice(0, 3).toUpperCase()}`}
          color="#B89C3A" Icon={Sparkles} />);
        break;
      case "resistance":
        chips.push(<Chip key={"gse" + i} testid={`chip-resistance-${eff.element}`} kind="element-resistance"
          label={`+${val}% ${eff.element} Res`} color={elColor(eff.element)} Icon={Shield} />);
        break;
      case "weakness":
        chips.push(<Chip key={"gse" + i} testid={`chip-weakness-${eff.element}`} kind="weakness"
          label={`-${val}% ${eff.element}`} color="#E33333" Icon={Skull} />);
        break;
      case "max_hp":
        chips.push(<Chip key={"gse" + i} testid="chip-max-hp" kind="max-hp"
          label={`+${val} HP MAX`} color="#8A0303" Icon={Heart} />);
        break;
      case "max_mana":
        chips.push(<Chip key={"gse" + i} testid="chip-max-mana" kind="max-mana"
          label={`+${val} Mana MAX`} color="#4A7BC7" Icon={Sparkles} />);
        break;
      case "max_vigor":
        chips.push(<Chip key={"gse" + i} testid="chip-max-vigor" kind="max-vigor"
          label={`+${val} Vigor MAX`} color="#6E8B3D" Icon={Zap} />);
        break;
      case "max_conc":
        chips.push(<Chip key={"gse" + i} testid="chip-max-conc" kind="max-conc"
          label={`+${val} CON`} color="#B89C3A" Icon={Sparkles} />);
        break;
      case "element_damage":
        chips.push(<Chip key={"gse" + i} testid={`chip-el-dmg-${eff.element}`} kind="element-damage"
          label={`+${val} ${eff.element}`} color={elColor(eff.element)} Icon={Flame} />);
        break;
      case "remove_dot":
        chips.push(<Chip key={"gse" + i} testid="chip-remove-dot" kind="remove-dot"
          label="Rimuove DoT" color="#6E8B3D" Icon={Sparkles} />);
        break;
      case "remove_status":
        chips.push(<Chip key={"gse" + i} testid="chip-remove-status" kind="remove-status"
          label={`Rimuove ${eff.status || "status"}`} color="#6E8B3D" Icon={Sparkles} />);
        break;
      default:
        break;
    }
  });
  if (chips.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${compact ? "mt-1" : "mt-2"}`}>
      {chips}
    </div>
  );
}

/** Standalone requirements chip — used by the Pack cards. */
export function RequirementsChip({ requirements }) {
  if (!requirements || requirements === "—" || requirements === "-") return null;
  return (
    <div data-testid="chip-requirements"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#8c8c8c] text-[#8c8c8c] font-mono text-[9px] tracking-wider uppercase whitespace-nowrap rounded-sm">
      Req: {requirements}
    </div>
  );
}
